import {CreateDirectDebitRequest} from "../../requests/payments/CreateDirectDebitRequest";
import {CreateDirectDebitFileRequest} from "../../requests/payments/CreateDirectDebitFileRequest";
import {CreateNewDirectDebitsBankFileRequest} from "../../requests/payments/CreateNewDirectDebitsBankFileRequest";
import {DirectDebitFile} from "../../model/payments/DirectDebitFile";
import {DirectDebitRun} from "../../model/payments/DirectDebitRun";
import {DirectDebitMemberProfile} from "../../model/payments/DirectDebitMemberProfile";
import { ProcessDirectDebitRejectionRequest } from "../../requests/payments/ProcessDirectDebitRejectionRequest";
import { CompleteDirectDebitRunRequest } from "../../requests/payments/CompleteDirectDebitRunRequest";
import { UpdateDirectDebitRunRequest } from "../../requests/payments/UpdateDirectDebitRunRequest";
import {ApiError, ClubBookingApiClient} from "../../common/api/ClubBookingApiClient";
import {DirectDebitRunPayment} from "../../model/payments/DirectDebitRunPayment";
import { DisplayDirectDebitTransaction } from "../../model/payments/DisplayDirectDebitTransaction";
// import {DirectDebitInstruction} from "../../model/payments/DirectDebitInstruction";

export class PaymentService extends ClubBookingApiClient {
    async createDirectDebit(request: CreateDirectDebitRequest): Promise<void> {
        await this.post('/payments/create/instruction', request, {successMessage: 'Direct debit created successfully'});
    }

    async getDirectDebitInstruction(membershipId: string): Promise<DirectDebitMemberProfile | null> {
        try {
            return await this.get<DirectDebitMemberProfile>(`/payments/directdebit/instruction/${membershipId}`);
        } catch (error) {
            // If no instruction found, return null
            return null;
        }
    }

    // successMessage is overridable because the same endpoint both saves and REMOVES a
    // mandate (clearing every field): "updated successfully" is the wrong words for a
    // removal, and the client raises this toast itself, so the caller can't correct it
    // afterwards without showing two.
    async updateDirectDebitInstruction(
        membershipId: string,
        request: CreateDirectDebitRequest,
        successMessage = 'Direct debit instruction updated successfully',
    ): Promise<void> {
        await this.post(`/payments/directdebit/instruction/update/${membershipId}`, request, {successMessage});
    }

    async getMyPayments(): Promise<DirectDebitRunPayment[]> {
        return await this.get<DirectDebitRunPayment[]>('/members/me/payments');
    }

    // Swallowing variant: any failure reads as "no mandate". Kept for callers that
    // only need something to render (the DD panel); prefer the OrThrow variant when
    // "we couldn't ask" must not be mistaken for "nothing on file".
    async getMyDirectDebitInstruction(): Promise<DirectDebitMemberProfile | null> {
        try {
            return await this.getMyDirectDebitInstructionOrThrow();
        } catch (error) {
            return null;
        }
    }

    // Same endpoint, but a failure THROWS instead of resolving to null/undefined.
    // Needed where "we couldn't ask" must not be read as "no mandate on file" —
    // the profile-completion banner would otherwise tell a member with a perfectly
    // good mandate to re-enter their bank details after a transient 403/500.
    // Quiet by design (`rethrow`, not `showError`): this is a background probe, so
    // the caller owns the failure UX rather than firing a toast on page load.
    async getMyDirectDebitInstructionOrThrow(): Promise<DirectDebitMemberProfile | null> {
        try {
            const profile = await this.get<DirectDebitMemberProfile>(
                '/members/me/directdebit',
                undefined,
                { rethrow: true, showLoading: false },
            );
            // An empty 200 body resolves to undefined — genuinely nothing on file.
            return profile ?? null;
        } catch (error) {
            // Defensive: today this endpoint always 200s with blank/placeholder
            // instruction fields for a member with no mandate (see ApiConfiguration.cs
            // GetMyDirectDebit), so a 404 isn't expected — but if that ever changes it
            // is the server saying "no mandate", an answer rather than a failure.
            // Everything else (403 / 5xx / network) propagates so the caller can hold
            // "unknown" rather than assert "none on file".
            if (error instanceof ApiError && error.status === 404) return null;
            throw error;
        }
    }

    // `showSuccess: false` lets a caller own the success UX — the profile-completion
    // wizard saves several sections and raises a single notification for the lot.
    async updateMyDirectDebitInstruction(request: CreateDirectDebitRequest, options?: { showSuccess?: boolean; showError?: boolean }): Promise<void> {
        await this.post('/members/me/directdebit/update', request, {successMessage: 'Direct debit details updated successfully', ...options});
    }

    async createDirectDebitFile(request: CreateDirectDebitFileRequest): Promise<Blob> {
        console.log('Creating direct debit file for', request.directDebitPaymentRequests.length, 'members on', request.paymentDate.toDateString());
        const directDebitFile = await this.post<DirectDebitFile>('/payments/create/directdebitfile', request, {successMessage: 'Direct debit file created successfully'});
        
        // Format each line to ensure sort code (6 chars) and account number (8 chars) are properly padded
        const formattedLines = directDebitFile.directDebitLines.map(line => {
            const parts = line.split(',');
            if (parts.length >= 2) {
                // Pad sort code to 6 characters with leading zeros
                const sortCode = parts[0].trim().padStart(6, '0');
                // Pad account number to 8 characters with leading zeros
                const accountNumber = parts[1].trim().padStart(8, '0');
                
                // Replace the first two parts with padded values
                parts[0] = sortCode;
                parts[1] = accountNumber;
            }
            return parts.join(',');
        });
        
        const fileContent = formattedLines.join('\n');
        return new Blob([fileContent], { type: 'text/csv;charset=utf-8' });
    }

    // showError on both: these drive the billing screens, where an empty list and a failed
    // load look identical but mean opposite things — "nothing to collect today" versus "we
    // couldn't ask". The base client resolves a failure to undefined, so without this the
    // operator gets a blank run selector on a collection day and no reason for it.
    async getDirectDebitRuns(): Promise<DirectDebitRun[]> {
        return this.get<DirectDebitRun[]>(
            '/payments/directdebit/runs',
            undefined,
            { showError: true },
        );
    }

    async getActiveDirectDebitMembers(): Promise<DirectDebitMemberProfile[]> {
        return this.get<DirectDebitMemberProfile[]>(
            '/payments/directdebit/active',
            undefined,
            { showError: true },
        );
    }

    async processDirectDebitRejection(request: ProcessDirectDebitRejectionRequest): Promise<DirectDebitRun> {
        console.log('Processing direct debit rejection:', request);
        return await this.post<DirectDebitRun>('/payments/directdebit/rejections', request, {successMessage: 'Direct debit rejection processed successfully'});
    }

    async completeDirectDebitRun(request: CompleteDirectDebitRunRequest): Promise<DirectDebitRun> {
        console.log('Completing direct debit run:', request);
        return await this.post<DirectDebitRun>('/payments/directdebit/complete', request, {successMessage: 'Direct debit run completed successfully'});
    }

    async getAllPayments(membershipId: string): Promise<DirectDebitRunPayment[]> {
        return await this.get<DirectDebitRunPayment[]>(`/payments/directdebit/allpayments/${membershipId}`);
    }

    async processPayment(request: any): Promise<void> {
        await this.post('/payments/markaspaid', request, {successMessage: 'Payment processed successfully'});
    }

    async updateDirectDebitRun(request: UpdateDirectDebitRunRequest): Promise<DirectDebitRun> {    
        return await this.post<DirectDebitRun>('/payments/directdebit/update', request, {successMessage: 'Direct debit run updated successfully'});
    }

    async getDirectDebitTransactions(from: string, to: string): Promise<DisplayDirectDebitTransaction[]> {
        return this.get<DisplayDirectDebitTransaction[]>('/payments/directdebit/transactions', { from, to });
    }

    async createNewDirectDebitsBankFile(request: CreateNewDirectDebitsBankFileRequest): Promise<Blob> {
        console.log('Creating new direct debits bank file as of:', request.asOfUtc);
        const directDebitFile = await this.post<DirectDebitFile>('/payments/create/newdirectdebits', request, {successMessage: 'New direct debits bank file created successfully'});
        
        // Format each line to ensure sort code (6 chars) and account number (8 chars) are properly padded
        const formattedLines = directDebitFile.directDebitLines.map(line => {
            const parts = line.split(',');
            if (parts.length >= 2) {
                // Pad sort code to 6 characters with leading zeros
                const sortCode = parts[0].trim().padStart(6, '0');
                // Pad account number to 8 characters with leading zeros
                const accountNumber = parts[1].trim().padStart(8, '0');
                
                // Replace the first two parts with padded values
                parts[0] = sortCode;
                parts[1] = accountNumber;
            }
            return parts.join(',');
        });
        
        const fileContent = formattedLines.join('\n');
        return new Blob([fileContent], { type: 'text/csv;charset=utf-8' });
    }
}